/**
 * Goals API — Supabase queries for goals CRUD.
 * All calls use .throwOnError() per project rules.
 */

import { z } from 'zod'
import { supabase } from '@turnifly/shared/api'
import { GoalSchema, GoalEntrySchema } from '@turnifly/shared/types'
import type { Goal, GoalCreate, GoalEntry } from '@turnifly/shared/types'

// ── Field whitelist for safe updates ──

const GOAL_UPDATE_WHITELIST = new Set([
  'name', 'icon', 'category', 'description', 'cover_image_url',
  'year', 'target_value', 'start_value', 'unit', 'direction',
  'goal_type', 'steps',
  'milestones', 'auto_milestones', 'weekly_milestones',
  'status', 'is_private', 'is_announced', 'monthly_reminder',
  'deadline', 'sort_order',
])

// ── Fetch goals by year ──

export async function fetchGoalsByYear(
  userId: string,
  year: number,
): Promise<Goal[]> {
  const { data } = await supabase
    .from('goals')
    .select('*')
    .eq('user_id', userId)
    .eq('year', year)
    .neq('status', 'archived')
    .order('sort_order')
    .order('created_at')
    .throwOnError()

  return z.array(GoalSchema).parse(data ?? [])
}

// ── Fetch single goal ──

export async function fetchGoalById(goalId: string): Promise<Goal> {
  const { data } = await supabase
    .from('goals')
    .select('*')
    .eq('id', goalId)
    .single()
    .throwOnError()

  return GoalSchema.parse(data)
}

// ── Create goal ──

export async function createGoal(
  userId: string,
  input: GoalCreate,
): Promise<Goal> {
  const { data } = await supabase
    .from('goals')
    .insert({ user_id: userId, ...input })
    .select('*')
    .single()
    .throwOnError()

  return GoalSchema.parse(data)
}

// ── Update goal (safe whitelist) ──

export async function updateGoal(
  goalId: string,
  updates: Record<string, unknown>,
): Promise<void> {
  const safeUpdates: Record<string, unknown> = {}
  for (const key of Object.keys(updates)) {
    if (GOAL_UPDATE_WHITELIST.has(key)) {
      safeUpdates[key] = updates[key]
    }
  }

  if (Object.keys(safeUpdates).length === 0) return

  safeUpdates.updated_at = new Date().toISOString()

  await supabase
    .from('goals')
    .update(safeUpdates)
    .eq('id', goalId)
    .throwOnError()
}

// ── Delete goal permanently ──

export async function deleteGoal(goalId: string): Promise<void> {
  // Delete entries first (CASCADE handles this, but explicit is safer)
  await supabase
    .from('goal_entries')
    .delete()
    .eq('goal_id', goalId)
    .throwOnError()

  await supabase
    .from('goals')
    .delete()
    .eq('id', goalId)
    .throwOnError()
}

// ── Fetch monthly progress from goal_entries ──

export interface MonthlyProgress {
  month: number
  achieved: number
}

export async function fetchGoalMonthlyProgress(
  goalId: string,
  year: number,
): Promise<MonthlyProgress[]> {
  const startDate = `${year}-01-01`
  const endDate = `${year}-12-31`

  const { data: entries } = await supabase
    .from('goal_entries')
    .select('entry_date, value')
    .eq('goal_id', goalId)
    .gte('entry_date', startDate)
    .lte('entry_date', endDate)
    .throwOnError()

  const monthMap = new Map<number, number>()
  for (const entry of (entries ?? []) as Array<{ entry_date: string; value: number }>) {
    const month = new Date(entry.entry_date + 'T00:00:00').getMonth() + 1
    monthMap.set(month, (monthMap.get(month) ?? 0) + entry.value)
  }

  const result: MonthlyProgress[] = []
  for (let m = 1; m <= 12; m++) {
    const achieved = monthMap.get(m)
    if (achieved !== undefined) {
      result.push({ month: m, achieved })
    }
  }

  return result
}

// ── Goal Entries CRUD ──

export async function fetchGoalEntries(
  goalId: string,
  limit: number = 50,
  offset: number = 0,
): Promise<GoalEntry[]> {
  const { data } = await supabase
    .rpc('get_goal_entries', {
      p_goal_id: goalId,
      p_limit: limit,
      p_offset: offset,
    })
    .throwOnError()

  return z.array(GoalEntrySchema).parse(data ?? [])
}

export async function createGoalEntry(
  userId: string,
  input: {
    goal_id: string
    value: number
    step_index?: number | null
    note: string
    entry_date: string
  },
): Promise<GoalEntry> {
  const { data } = await supabase
    .from('goal_entries')
    .insert({
      user_id: userId,
      goal_id: input.goal_id,
      value: input.value,
      step_index: input.step_index ?? null,
      note: input.note,
      entry_date: input.entry_date,
    })
    .select('*')
    .single()
    .throwOnError()

  return GoalEntrySchema.parse(data)
}

export async function deleteGoalEntry(entryId: string): Promise<void> {
  await supabase
    .from('goal_entries')
    .delete()
    .eq('id', entryId)
    .throwOnError()
}

export async function toggleGoalStep(
  userId: string,
  goalId: string,
  stepIndex: number,
  label: string,
  isCompleting: boolean,
): Promise<void> {
  if (isCompleting) {
    await supabase
      .from('goal_entries')
      .insert({
        user_id: userId,
        goal_id: goalId,
        value: 1,
        step_index: stepIndex,
        note: label,
        entry_date: new Date().toISOString().split('T')[0],
      })
      .throwOnError()
  } else {
    await supabase
      .from('goal_entries')
      .delete()
      .eq('goal_id', goalId)
      .eq('step_index', stepIndex)
      .throwOnError()
  }
}

export async function shareGoalEntry(
  userId: string,
  entry: GoalEntry,
  goal: Goal,
): Promise<void> {
  await supabase
    .from('posts')
    .insert({
      user_id: userId,
      type: 'goal_entry',
      content: goal.is_private ? null : entry.note,
      goal_id: goal.id,
      metadata: {
        goal_name: goal.is_private ? null : goal.name,
        goal_icon: goal.is_private ? '🔒' : goal.icon,
        goal_type: goal.goal_type,
        entry_value: entry.value,
        entry_note: entry.note,
        goal_unit: goal.unit,
        target_value: goal.target_value,
        is_private: goal.is_private,
      },
    })
    .throwOnError()

  await supabase
    .from('goal_entries')
    .update({ is_shared: true })
    .eq('id', entry.id)
    .throwOnError()
}

// ── Fetch all goals for a user profile (all years, non-archived) ──

export async function fetchProfileGoals(userId: string): Promise<Goal[]> {
  const { data } = await supabase
    .from('goals')
    .select('*')
    .eq('user_id', userId)
    .neq('status', 'archived')
    .order('year', { ascending: false })
    .order('status')
    .order('sort_order')
    .throwOnError()

  return z.array(GoalSchema).parse(data ?? [])
}

// ── Create goal achievement post (fire-and-forget) ──

export async function createGoalAchievementPost(
  userId: string,
  goal: Goal,
): Promise<void> {
  const daysTaken = goal.achieved_at
    ? Math.ceil((new Date(goal.achieved_at).getTime() - new Date(goal.created_at).getTime()) / (1000 * 60 * 60 * 24))
    : 0

  await supabase
    .from('posts')
    .insert({
      user_id: userId,
      type: 'goal_achieved',
      content: goal.is_private ? null : goal.name,
      goal_id: goal.id,
      metadata: {
        goal_name: goal.is_private ? null : goal.name,
        goal_icon: goal.is_private ? '🔒' : goal.icon,
        goal_type: goal.goal_type,
        goal_unit: goal.unit,
        target_value: goal.target_value,
        current_value: goal.current_value,
        days_taken: daysTaken,
        is_private: goal.is_private,
      },
    })
    .throwOnError()
}

// ── Announce Goal to Feed ──

export async function announceGoal(
  userId: string,
  goal: Goal,
): Promise<void> {
  // Create feed post
  await supabase
    .from('posts')
    .insert({
      user_id: userId,
      type: 'goal_announced',
      content: goal.is_private ? null : goal.name,
      goal_id: goal.id,
      metadata: {
        goal_name: goal.is_private ? null : goal.name,
        goal_icon: goal.is_private ? '🔒' : goal.icon,
        goal_type: goal.goal_type,
        goal_unit: goal.unit,
        target_value: goal.target_value,
        year: goal.year,
        is_private: goal.is_private,
      },
    })
    .throwOnError()

  // Mark goal as announced
  await supabase
    .from('goals')
    .update({ is_announced: true, updated_at: new Date().toISOString() })
    .eq('id', goal.id)
    .throwOnError()
}

// ── Goal Wishes ──

export interface GoalWish {
  id: string
  goal_id: string
  user_id: string
  text: string
  created_at: string
  profile?: {
    full_name: string
    avatar_url: string | null
  }
}

export async function fetchGoalWishes(goalId: string): Promise<GoalWish[]> {
  const { data: wishes } = await supabase
    .from('goal_wishes')
    .select('id, goal_id, user_id, text, created_at')
    .eq('goal_id', goalId)
    .order('created_at', { ascending: false })
    .throwOnError()

  const rows = (wishes ?? []) as Array<Record<string, unknown>>
  if (rows.length === 0) return []

  // Batch-fetch profiles for wish authors
  const userIds = [...new Set(rows.map((w) => String(w.user_id)))]
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url')
    .in('id', userIds)
    .throwOnError()

  const profileMap = new Map(
    ((profiles ?? []) as Array<{ id: string; full_name: string; avatar_url: string | null }>)
      .map((p) => [p.id, { full_name: p.full_name, avatar_url: p.avatar_url }]),
  )

  return rows.map((w) => ({
    id: String(w.id),
    goal_id: String(w.goal_id),
    user_id: String(w.user_id),
    text: String(w.text),
    created_at: String(w.created_at),
    profile: profileMap.get(String(w.user_id)),
  }))
}

export async function createGoalWish(
  goalId: string,
  userId: string,
  text: string,
): Promise<void> {
  await supabase
    .from('goal_wishes')
    .insert({ goal_id: goalId, user_id: userId, text })
    .throwOnError()
}

export async function deleteGoalWish(wishId: string): Promise<void> {
  await supabase
    .from('goal_wishes')
    .delete()
    .eq('id', wishId)
    .throwOnError()
}
